import { type ReactElement } from 'react';
import { NotificationSeverityLevel } from '@kirin/api-client/contracts/notification-settings';
import { Severity } from '@kirin/api-client/contracts/shared/severity';
import { SeverityBadge } from './policylist/components/SeverityBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { SeveritySelectTrigger } from '@/components/ui/severity-select-trigger';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/hooks/api/errorHelpers';
import { useIsOwnerOrManager } from '@/hooks/useRoleCheck';
import {
  useUserNotificationSettings,
  useUpdateUserNotificationSettings,
  useOrgNotificationSettings,
  useUpdateOrgNotificationSettings,
} from '@/hooks/api/useNotificationSettings';
import {
  getLastNotificationSeverity,
  setLastNotificationSeverity,
} from '@/lib/lastNotificationSeverity';

interface NotificationSettingsControlProps {
  /** Organization ID. If undefined, shows personal settings. */
  organizationId?: string;
}

/** Unboxed badge that reads like the plain-text options below it: same size,
 *  weight, and color (icon included) instead of the severity tint. */
const SEVERITY_OPTION_CLASS =
  'bg-transparent border-0 p-0 text-sm font-normal text-inherit';

/**
 * Control for configuring alert notification settings.
 *
 * - Personal context (no organizationId): User controls their own notification preference
 * - Organization context (with organizationId): Owners/managers can edit, members view-only
 *
 * This setting controls interactive popup/toast notifications, not passive indicators
 * like status bar icons, logs, or the web dashboard.
 */
export function NotificationSettingsControl({
  organizationId,
}: NotificationSettingsControlProps): ReactElement {
  const { toast } = useToast();

  // Determine if user can edit (personal is always editable, org requires owner/manager role)
  const { isOwnerOrManager, isLoading: isRoleCheckLoading } =
    useIsOwnerOrManager(organizationId);
  const canEdit = organizationId ? isOwnerOrManager : true;

  // Note: All hooks must be called unconditionally (React rules of hooks).
  // We instantiate both user and org hooks, then select the appropriate one based on context.
  // The org query has `enabled: !!organizationId` to prevent fetching when not needed.
  // The org mutation exists but is never called when organizationId is undefined.
  const userQuery = useUserNotificationSettings();
  const orgQuery = useOrgNotificationSettings(organizationId ?? '');
  const userMutation = useUpdateUserNotificationSettings();
  const orgMutation = useUpdateOrgNotificationSettings(organizationId ?? '');

  // Select data and mutation based on context
  const query = organizationId ? orgQuery : userQuery;
  const mutation = organizationId ? orgMutation : userMutation;

  // Mirrors backend virtual defaults in get-notification-settings.ts:
  // personal → High, org → ActionsOnly.
  const defaultSeverity = organizationId
    ? NotificationSeverityLevel.ActionsOnly
    : NotificationSeverityLevel.High;
  const currentValue = query.data?.minAlertSeverity ?? defaultSeverity;
  // Toggle is a derived view of severity: "on" = any non-none severity, "off" = 'none'.
  // No separate enabled flag exists on the contract; off persists as minAlertSeverity:'none'.
  const isEnabled = currentValue !== NotificationSeverityLevel.None;

  const persistSeverity = (next: NotificationSeverityLevel): void => {
    if (!canEdit) return;
    mutation.mutate(
      { minAlertSeverity: next },
      {
        onSuccess: () => {
          setLastNotificationSeverity(organizationId, next);
          toast({
            title: 'Settings saved',
            description: 'Notification settings updated successfully',
          });
        },
        onError: error => {
          toast({
            title: 'Error saving settings',
            description: getErrorMessage(error),
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleChange = (value: string): void => {
    persistSeverity(value as NotificationSeverityLevel);
  };

  const handleToggle = (next: boolean): void => {
    if (!next) {
      // onSuccess save path no-ops on `none`; cache here so toggle-on can restore.
      setLastNotificationSeverity(organizationId, currentValue);
      persistSeverity(NotificationSeverityLevel.None);
      return;
    }
    const restored =
      getLastNotificationSeverity(organizationId) ?? defaultSeverity;
    persistSeverity(restored);
  };

  // Loading state
  if (query.isLoading || isRoleCheckLoading) {
    return (
      <Card>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Loading settings...
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state - show error message with option to use defaults
  if (query.isError) {
    return (
      <Card>
        <CardContent>
          <div className="rounded-md bg-destructive/15 p-4">
            <p className="text-sm text-destructive font-medium">
              Failed to load notification settings
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Please try again later or contact support if the problem persists.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4">
        {/* Read-only message for org members */}
        {organizationId && !canEdit && (
          <p className="text-sm text-muted-foreground">
            Only owners and managers can change this setting.
          </p>
        )}

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="alertNotificationsEnabled">
              {organizationId
                ? 'Organization Alert Notifications'
                : 'Alert Notifications'}
            </Label>
            <p className="text-sm text-muted-foreground">
              Receive in-app popup notifications for security alerts
            </p>
          </div>
          <Switch
            id="alertNotificationsEnabled"
            checked={isEnabled}
            onCheckedChange={handleToggle}
            disabled={!canEdit || mutation.isPending}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="minAlertSeverity">Minimum Alert Severity</Label>

          <Select
            value={currentValue}
            onValueChange={handleChange}
            disabled={!canEdit || !isEnabled || mutation.isPending}
          >
            <SeveritySelectTrigger id="minAlertSeverity">
              <SelectValue />
            </SeveritySelectTrigger>
            <SelectContent>
              <SelectItem value={NotificationSeverityLevel.Low}>
                <SeverityBadge
                  riskLevel={Severity.Low}
                  showIcon
                  label="Low - Notify on all alerts"
                  className={SEVERITY_OPTION_CLASS}
                />
              </SelectItem>
              <SelectItem value={NotificationSeverityLevel.Medium}>
                <SeverityBadge
                  riskLevel={Severity.Medium}
                  showIcon
                  label="Medium - Notify on medium, high, and critical"
                  className={SEVERITY_OPTION_CLASS}
                />
              </SelectItem>
              <SelectItem value={NotificationSeverityLevel.High}>
                <SeverityBadge
                  riskLevel={Severity.High}
                  showIcon
                  label="High - Notify on high and critical only"
                  className={SEVERITY_OPTION_CLASS}
                />
              </SelectItem>
              <SelectItem value={NotificationSeverityLevel.Critical}>
                <SeverityBadge
                  riskLevel={Severity.Critical}
                  showIcon
                  label="Critical - Notify on critical only"
                  className={SEVERITY_OPTION_CLASS}
                />
              </SelectItem>
              <SelectItem value={NotificationSeverityLevel.ActionsOnly}>
                🛡️ Actions only - Notify only when action is taken
              </SelectItem>
              <SelectItem value={NotificationSeverityLevel.None}>
                🔕 Do not notify on alerts
              </SelectItem>
            </SelectContent>
          </Select>

          <p className="text-sm text-muted-foreground">
            Only show notifications for alerts at or above this severity
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
